import {useCallback, useMemo, useReducer, useState} from 'react';
import {toast} from 'react-toastify';
import {createColumnHelper, getCoreRowModel, useReactTable, type Row} from '@tanstack/react-table';
import {format} from 'date-fns';
import {queryClient} from 'App';
import {imgNone} from 'assets';
import {useCheckPlan} from 'hooks';
import {classNames, fileSize} from 'utils';
import {getGetMeshesQueryKey, useDeleteMeshes, useGetMeshes, type MeshType} from 'api/generated';
import {Button, DropdownSingle, InputCheckbox, ModalModelUpdate, ModalModelUpload, TableCommon, Transitioner} from 'components';

const columnHelper = createColumnHelper<MeshType>();

interface IAssetsModelsProps {
  filter: 'mine' | 'collab';
  dropdownItems: {id: string; label: string; onClick: () => void}[];
  animate?: boolean;
}

const AssetsModels = ({filter, dropdownItems, animate = true}: IAssetsModelsProps) => {
  const [showModalUpload, toggleModalUpload] = useReducer(s => !s, false);
  const [showModalUpdate, toggleModalUpdate] = useReducer(s => !s, false);
  const [mesh, setMesh] = useState<MeshType>();

  const {data: meshesData, isFetching} = useGetMeshes({filter}, {query: {staleTime: 5 * 1000, enabled: !!filter}});
  const meshes = useMemo(() => meshesData?.data?.data ?? [], [meshesData?.data?.data]);

  const handleUploaded = useCallback((mesh: MeshType) => {
    queryClient.invalidateQueries({queryKey: [getGetMeshesQueryKey({filter: 'mine'})[0]]});
    setMesh(mesh);
    toggleModalUpload();
    toggleModalUpdate();
  }, []);

  const deleteMeshes = useDeleteMeshes();
  const handleDeleteFiles = async () => {
    const uids = table.getSelectedRowModel().rows.map(r => r.original.uid);
    const {data: res} = await deleteMeshes.mutateAsync({data: {uids}});
    if (res.data?.success) {
      toast.success('Models deleted');
      table.resetRowSelection();
      queryClient.invalidateQueries({queryKey: [getGetMeshesQueryKey({filter: 'mine'})[0]]});
    } else if (res.data?.message) {
      toast.error(res.data.message);
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => {
          const numSelected = table.getSelectedRowModel().rows.length;
          if (numSelected === 0) {
            return (
              <InputCheckbox checked={table.getIsAllPageRowsSelected()} onChange={table.getToggleAllPageRowsSelectedHandler()}>
                <label className="ml-22">
                  <span>File name</span>
                </label>
              </InputCheckbox>
            );
          }
          return (
            <InputCheckbox checked={table.getIsAllPageRowsSelected()} onChange={table.getToggleAllPageRowsSelectedHandler()}>
              <span className="w-20 flex gap-1">
                <span className="w-10 text-right">{numSelected}</span> selected
              </span>
              <Button text="Delete files" size="extra-small" variant="alert" onClick={handleDeleteFiles} />
            </InputCheckbox>
          );
        },
        cell: info => (
          <InputCheckbox checked={info.row.getIsSelected()} onChange={info.row.getToggleSelectedHandler()}>
            <div className="group flex gap-3 items-center min-w-50">
              <img src={info.row.original.imageUrl ?? imgNone} alt={info.getValue()} className="w-18 aspect-square object-cover rounded-md" />
              <div className="flex flex-col items-start gap-0.5">
                <span className="text-slate-800 dark:text-white">{info.getValue()}</span>
                <span className="text-slate-500 dark:text-slate-400">{info.row.original.fileFormat!.toUpperCase()}</span>
              </div>
            </div>
          </InputCheckbox>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: () => <span>Date added</span>,
        cell: info => <span>{format(new Date(info.getValue()!), 'd MMM y')}</span>,
      }),
      ...(filter === 'mine'
        ? [
            columnHelper.accessor('uploader', {
              header: () => <span>Uploader</span>,
              cell: info => <span>{info.getValue()?.name === name ? 'Me' : info.getValue()?.name}</span>,
            }),
          ]
        : [
            columnHelper.accessor('owner', {
              header: () => <span>Owner</span>,
              cell: info => <span>{info.getValue()?.name === name ? 'Me' : info.getValue()?.name}</span>,
            }),
          ]),
      columnHelper.accessor('byteSize', {
        header: () => <span>Size</span>,
        cell: info => <span>{fileSize(info.getValue()!)}</span>,
      }),
      columnHelper.accessor('viewCount', {
        header: () => <span>Viewed</span>,
        cell: info => <span>{info.getValue() === 0 ? '-' : `${info.getValue()} times`}</span>,
      }),
      columnHelper.accessor('projectsCount', {
        header: () => <span>References</span>,
        cell: info => {
          const noRef = info.getValue() === 0;
          return (
            <span
              className={classNames(!noRef && 'text-primary hover:brightness-120 cursor-pointer')}
              // TODO: Implement this
              onClick={() => !noRef && console.info('show linked projects modal')}>
              {noRef ? '-' : `${info.getValue()} projects`}
            </span>
          );
        },
      }),
    ],
    [filter],
  );

  const table = useReactTable({
    data: meshes,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = useCallback((row: Row<MeshType>) => {
    setMesh(row.original);
    toggleModalUpdate();
  }, []);

  const description = useMemo(() => {
    if (filter === 'mine') return 'Models owned by me.\nThey contribute to my storage usage.';
    if (filter === 'collab') return 'Models uploaded by me for collaborated projects.\nThey are owned by the project owners.';
    return '';
  }, [filter]);

  const {isLimitReached} = useCheckPlan({
    featureId: 'models',
    data: meshes,
    isDataFetching: isFetching,
  });

  return (
    <Transitioner disabled={!animate}>
      <div className="flex items-center justify-between">
        <div className="sm:flex-auto">
          <h1 className="font-medium text-lg dark:text-white">Assets</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-darkText hidden sm:block whitespace-pre-line">{description}</p>
        </div>
        {filter === 'mine' && !isLimitReached && <Button text="+ New model" size="small" onClick={toggleModalUpload} />}
      </div>
      <ModalModelUpload visible={showModalUpload} onClose={toggleModalUpload} onUploaded={handleUploaded} />
      <ModalModelUpdate visible={showModalUpdate} onClose={toggleModalUpdate} mesh={mesh} />
      <Transitioner delay={0.1} disabled={!animate}>
        <TableCommon
          table={table}
          onRowClick={handleRowClick}
          filters={
            <div className="flex justify-end">
              <DropdownSingle
                items={dropdownItems}
                selected={
                  dropdownItems.find(i => {
                    const [_filter, type] = i.id.split('-');
                    return _filter === filter && type === 'model';
                  })!
                }
              />
            </div>
          }
        />
      </Transitioner>
    </Transitioner>
  );
};

export default AssetsModels;
